import { TextareaAutosize as MuiTextarea } from '@mui/material';
import { cn } from 'cn';
import { forwardRef, type TextareaHTMLAttributes, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly flex?: '1' | 'auto' | 'initial' | 'none';
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly minRows?: number;
  readonly maxRows?: number;
} & TextareaHTMLAttributes<HTMLTextAreaElement> &
  ComponentStyling;

export const TextareaField = forwardRef<HTMLTextAreaElement, P>(
  (
    {
      className,
      flex = '1',
      hint,
      isDisabled = false,
      label,
      maxRows = 10,
      minRows = 1,
      onBlur,
      placeholder,
      style,
      ...rest
    },
    ref,
  ) => {
    const field = useFieldContext<string>();

    const isRequired = isFieldRequired(
      field.options.form.options.validators,
      field.name,
    );

    const hasErrors = Boolean(field.state.meta.errors?.length);

    const id = useId();

    return (
      <div className={cn('relative', className)}>
        {Boolean(label) && (
          <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
            {label}
          </InputLabel>
        )}
        <div
          className={cn(
            'overflow-hidden transition-colors duration-200 ease-out',
            {
              //   'border-tigaBorder-default focus-within:border-tigaAction-lighter hover:border-tigaAction-lighter':
              //     !(hasErrors || disabled),

              //   'border-tigaBorder-darker text-tigaText-lighter cursor-not-allowed':
              //     disabled,
              'flex-auto': flex === 'auto',
              'flex-initial': flex === 'initial',
              'flex-none': flex === 'none',
            },
          )}
          style={style}
        >
          <MuiTextarea
            className={cn(
              `box-border min-h-10 w-full flex-1 rounded-sm border border-tiga-border-default bg-white px-[14px] py-2 placeholder:pr-1 placeholder:text-tiga-text-lighter placeholder:italic focus:outline-none`,
              {
                'border-tiga-semantic-error': hasErrors,
              },
            )}
            disabled={isDisabled}
            id={id}
            maxRows={maxRows}
            minRows={minRows}
            placeholder={placeholder}
            ref={ref}
            value={field.state.value ?? ''}
            onBlur={(event) => {
              field.handleBlur();
              onBlur?.(event);
            }}
            onChange={(event) => {
              field.handleChange(event.target.value);
            }}
            {...rest}
          />
        </div>
        {hasErrors ? (
          <ValidationError>
            {unique(field.state.meta.errors.map(({ message }) => message)).join(
              ', ',
            )}
          </ValidationError>
        ) : null}
      </div>
    );
  },
);

TextareaField.displayName = 'TextareaField';
